import React, { useEffect, useState } from 'react';
import { LoaderCircle, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../contexts/ToastContext';
import actionItemService from '../../services/actionItemService';

const ActionItemCommentsFeed = ({ itemId, canAdd }) => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [comments, setComments] = useState([]);
  const [draft, setDraft] = useState('');
  const [nextPage, setNextPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const loadComments = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await actionItemService.listComments(itemId, 1);
        if (!active) return;
        setComments(response.results || []);
        setNextPage(response.next ? 2 : null);
      } catch (requestError) {
        if (active) setError(t('actionItems.errors.commentsLoad'));
      } finally {
        if (active) setLoading(false);
      }
    };

    loadComments();
    return () => {
      active = false;
    };
  }, [itemId, t]);

  const displayAuthor = (author) => {
    const name = `${author?.first_name || ''} ${
      author?.last_name || ''
    }`.trim();
    return name || author?.email || t('actionItems.emptyValue');
  };

  const formatDateTime = (value) => (
    new Intl.DateTimeFormat(i18n.language, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value))
  );

  const submitComment = async (event) => {
    event.preventDefault();
    const body = draft.trim();
    if (!body || submitting) return;
    setSubmitting(true);
    try {
      const created = await actionItemService.addComment(itemId, body);
      setComments((current) => [created, ...current]);
      setDraft('');
      toast.success(t('actionItems.commentAdded'));
    } catch (requestError) {
      toast.error(
        requestError.response?.data?.detail
          || t('actionItems.errors.commentsAdd'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const loadMore = async () => {
    if (!nextPage || loadingMore) return;
    setLoadingMore(true);
    try {
      const response = await actionItemService.listComments(
        itemId,
        nextPage,
      );
      setComments((current) => [
        ...current,
        ...(response.results || []),
      ]);
      setNextPage(response.next ? nextPage + 1 : null);
    } catch (requestError) {
      toast.error(t('actionItems.errors.commentsLoad'));
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <section className="action-item-detail-card">
      <h2>{t('actionItems.commentsAc')}</h2>

      {canAdd && (
        <form
          className="action-item-comment-composer"
          onSubmit={submitComment}
        >
          <label className="action-item-field">
            <span>{t('actionItems.addComment')}</span>
            <textarea
              rows="4"
              value={draft}
              aria-label={t('actionItems.addComment')}
              placeholder={t('actionItems.commentPlaceholder')}
              onChange={(event) => setDraft(event.target.value)}
            />
          </label>
          <button
            className="action-items-primary-button action-item-full-button"
            type="submit"
            disabled={!draft.trim() || submitting}
          >
            <Send size={17} aria-hidden="true" />
            {t('actionItems.submitComment')}
          </button>
        </form>
      )}

      {!canAdd && (
        <p className="action-item-help">
          {t('actionItems.commentsReadOnly')}
        </p>
      )}

      {loading && (
        <div className="action-items-inline-loading">
          <LoaderCircle size={17} aria-hidden="true" />
          {t('common.loading')}
        </div>
      )}

      {error && (
        <p className="action-items-alert" role="alert">{error}</p>
      )}

      {!loading && !error && comments.length === 0 && (
        <p className="action-item-comments-empty">
          {t('actionItems.noComments')}
        </p>
      )}

      {!loading && comments.length > 0 && (
        <div className="action-item-comments-feed">
          {comments.map((comment) => (
            <article className="action-item-comment" key={comment.id}>
              <header>
                <strong>{displayAuthor(comment.author)}</strong>
                <time dateTime={comment.created_at}>
                  {formatDateTime(comment.created_at)}
                </time>
              </header>
              <p>{comment.body}</p>
            </article>
          ))}
        </div>
      )}

      {nextPage && (
        <button
          className="action-items-secondary-button action-item-full-button"
          type="button"
          disabled={loadingMore}
          onClick={loadMore}
        >
          {t('actionItems.loadMoreComments')}
        </button>
      )}
    </section>
  );
};

export default ActionItemCommentsFeed;
